import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Tooltip,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import GET_BOT from "src/graphql/query/chatbot/getBot";
import CREATE_BOT from "src/graphql/mutation/chatbot/createBot";
import UPDATE_BOT from "src/graphql/mutation/chatbot/updateBot";
import DEPLOY_BOT from "src/graphql/mutation/chatbot/deployBot";
import UPDATE_DEPLOYMENT from "src/graphql/mutation/chatbot/updateDeployment";
import { useSnackbar } from "notistack";
import ConfigurationTab from "./shared/ConfigurationTab";
import KnowledgeBaseTab from "./shared/KnowledgeBaseTab";
import InstallTab from "./shared/InstallTab";
import { ChatbotConfig, defaultChatbotConfig } from "./shared/types";
import useStyles from "./Styles";

const WidgetEditor: React.FC = () => {
  const { classes } = useStyles();
  const { widgetId } = useParams<{ widgetId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(
    (location.state as any)?.activeTab || 0,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if ((location.state as any)?.activeTab !== undefined) {
      setActiveTab((location.state as any).activeTab);
    }
  }, [location.state]);

  const isNewWidget = widgetId === "create";

  const { data, loading } = useQuery(GET_BOT, {
    variables: { botId: widgetId },
    skip: isNewWidget,
    fetchPolicy: "network-only",
  });

  const [createBotMutation] = useMutation(CREATE_BOT);
  const [updateBotMutation] = useMutation(UPDATE_BOT);
  const [deployBotMutation] = useMutation(DEPLOY_BOT);
  const [updateDeploymentMutation] = useMutation(UPDATE_DEPLOYMENT);

  const [config, setConfig] = useState<ChatbotConfig>({
    ...defaultChatbotConfig,
    id: isNewWidget ? "create" : widgetId || "",
    entityId: isNewWidget ? "create" : widgetId || "",
    entityType: "widget",
    name: "", // Explicitly initialize
    description: "", // Explicitly initialize
  } as ChatbotConfig);

  // Sync with backend data
  React.useEffect(() => {
    if (data?.getBot) {
      const bot = data.getBot;
      const widgetDeployment = bot.deployments?.find(
        (d: any) => d.channelType === "widget",
      );

      setConfig({
        ...defaultChatbotConfig,
        id: bot.botId,
        entityId: bot.botId,
        name: bot.name,
        description: bot.description || "",
        enabled: bot.enabled,
        ai: {
          systemPrompt:
            bot.systemPrompt || defaultChatbotConfig.ai.systemPrompt,
          language: bot.language || "en",
          fallbackMessage:
            bot.fallbackMessage || defaultChatbotConfig.ai.fallbackMessage,
        },
        escalation: {
          enabled: bot.handoffEnabled,
          handoffAfterMessages: bot.handoffAfterMessages || 5,
          handoffConfidenceThreshold: parseFloat(
            bot.handoffConfidenceThreshold || 0.5,
          ),
        },
        appearance: {
          primaryColor:
            bot.primaryColor || defaultChatbotConfig.appearance.primaryColor,
          secondaryColor:
            bot.secondaryColor ||
            defaultChatbotConfig.appearance.secondaryColor,
          backgroundColor:
            bot.backgroundColor ||
            defaultChatbotConfig.appearance.backgroundColor,
          borderRadius:
            bot.borderRadius ?? defaultChatbotConfig.appearance.borderRadius,
          position: bot.position || defaultChatbotConfig.appearance.position,
          avatarUrl: bot.avatarUrl || defaultChatbotConfig.appearance.avatarUrl,
          launcherImageUrl:
            bot.launcherImageUrl ||
            defaultChatbotConfig.appearance.launcherImageUrl,
          darkMode: bot.darkMode ?? defaultChatbotConfig.appearance.darkMode,
        },
        behavior: {
          welcomeMessage:
            bot.welcomeMessage || defaultChatbotConfig.behavior.welcomeMessage,
          placeholderText:
            bot.placeholderText ||
            defaultChatbotConfig.behavior.placeholderText,
          autoOpen: bot.autoOpen ?? defaultChatbotConfig.behavior.autoOpen,
          autoOpenDelay:
            bot.autoOpenDelay || defaultChatbotConfig.behavior.autoOpenDelay,
          typingAnimation:
            bot.typingAnimation ??
            defaultChatbotConfig.behavior.typingAnimation,
          soundEnabled:
            bot.soundEnabled ?? defaultChatbotConfig.behavior.soundEnabled,
          botTakeoverName:
            bot.botTakeoverName ||
            defaultChatbotConfig.behavior.botTakeoverName,
          agentTakeoverName:
            bot.agentTakeoverName ||
            defaultChatbotConfig.behavior.agentTakeoverName,
          handoffEnabled:
            bot.handoffEnabled ?? defaultChatbotConfig.behavior.handoffEnabled,
        },
        links: bot.links || [],
        vectorNamespace: widgetDeployment?.vectorNamespace || "",
      } as ChatbotConfig);
    }
  }, [data]);

  const handleSave = async (configToSave: ChatbotConfig) => {
    if (!configToSave.name || configToSave.name.trim() === "") {
      enqueueSnackbar("Please provide a name for the chatbot", {
        variant: "warning",
      });
      return;
    }

    try {
      setIsSaving(true);
      if (isNewWidget) {
        // 1. Create Bot
        const { data: botData } = await createBotMutation({
          variables: {
            input: {
              name: configToSave.name,
              description: configToSave.description,
              systemPrompt: configToSave.ai.systemPrompt,
              fallbackMessage: configToSave.ai.fallbackMessage,
              // UI Config
              primaryColor: configToSave.appearance.primaryColor,
              secondaryColor: configToSave.appearance.secondaryColor,
              backgroundColor: configToSave.appearance.backgroundColor,
              borderRadius: configToSave.appearance.borderRadius,
              position: configToSave.appearance.position,
              avatarUrl: configToSave.appearance.avatarUrl?.startsWith("blob:")
                ? ""
                : configToSave.appearance.avatarUrl,
              launcherImageUrl:
                configToSave.appearance.launcherImageUrl?.startsWith("blob:")
                  ? ""
                  : configToSave.appearance.launcherImageUrl,
              avatarFile: configToSave.appearance.avatarFile,
              launcherFile: configToSave.appearance.launcherFile,
              darkMode: configToSave.appearance.darkMode,
              welcomeMessage: configToSave.behavior.welcomeMessage,
              placeholderText: configToSave.behavior.placeholderText,
              autoOpen: configToSave.behavior.autoOpen,
              autoOpenDelay: configToSave.behavior.autoOpenDelay,
              typingAnimation: configToSave.behavior.typingAnimation,
              soundEnabled: configToSave.behavior.soundEnabled,
              botTakeoverName: configToSave.behavior.botTakeoverName,
              agentTakeoverName: configToSave.behavior.agentTakeoverName,
              enabled: configToSave.enabled,
              handoffEnabled: configToSave.behavior.handoffEnabled,
              links: configToSave.links,
            },
          },
        });

        if (botData?.createBot?.success) {
          const newBotId = botData.createBot.bot.botId;
          const serverBot = botData.createBot.bot;

          // 2. Automatically deploy as a website widget
          // Prepare clean appearance config (no File objects, and use server URLs)
          const cleanAppearance = {
            ...configToSave.appearance,
            avatarUrl: serverBot.avatarUrl || configToSave.appearance.avatarUrl,
            launcherImageUrl:
              serverBot.launcherImageUrl ||
              configToSave.appearance.launcherImageUrl,
            avatarFile: undefined,
            launcherFile: undefined,
          };

          await deployBotMutation({
            variables: {
              input: {
                botId: newBotId,
                channelType: "widget",
                channelId: `widget_${newBotId}`,
                channelName: `${configToSave.name} Website Widget`,
                widgetConfig: {
                  appearance: cleanAppearance,
                  behavior: configToSave.behavior,
                },
              },
            },
          });

          enqueueSnackbar("Bot created and deployed securely", {
            variant: "success",
          });
          // Redirect to the same bot but in edit mode, showing the Data Ingestion tab
          navigate(`/settings/chatbot-widgets/${newBotId}`, {
            state: { activeTab: 1 },
          });
        } else {
          enqueueSnackbar(botData?.createBot?.error || "Failed to create bot", {
            variant: "error",
          });
        }
      } else {
        // Update Bot
        const { data: updateData } = await updateBotMutation({
          variables: {
            botId: widgetId,
            input: {
              name: configToSave.name,
              description: configToSave.description,
              systemPrompt: configToSave.ai.systemPrompt,
              fallbackMessage: configToSave.ai.fallbackMessage,
              // UI Config
              primaryColor: configToSave.appearance.primaryColor,
              secondaryColor: configToSave.appearance.secondaryColor,
              backgroundColor: configToSave.appearance.backgroundColor,
              borderRadius: configToSave.appearance.borderRadius,
              position: configToSave.appearance.position,
              avatarUrl: configToSave.appearance.avatarUrl?.startsWith("blob:")
                ? ""
                : configToSave.appearance.avatarUrl,
              launcherImageUrl:
                configToSave.appearance.launcherImageUrl?.startsWith("blob:")
                  ? ""
                  : configToSave.appearance.launcherImageUrl,
              avatarFile: configToSave.appearance.avatarFile,
              launcherFile: configToSave.appearance.launcherFile,
              darkMode: configToSave.appearance.darkMode,
              welcomeMessage: configToSave.behavior.welcomeMessage,
              placeholderText: configToSave.behavior.placeholderText,
              autoOpen: configToSave.behavior.autoOpen,
              autoOpenDelay: configToSave.behavior.autoOpenDelay,
              typingAnimation: configToSave.behavior.typingAnimation,
              soundEnabled: configToSave.behavior.soundEnabled,
              botTakeoverName: configToSave.behavior.botTakeoverName,
              agentTakeoverName: configToSave.behavior.agentTakeoverName,
              enabled: configToSave.enabled,
              handoffEnabled: configToSave.behavior.handoffEnabled,
              links: configToSave.links,
            },
          },
        });

        const serverBot = updateData?.updateBot?.bot;

        // Update Deployment (for the widget)
        const widgetDeployment = data?.getBot?.deployments?.find(
          (d: any) => d.channelType === "widget",
        );
        if (widgetDeployment) {
          const cleanAppearance = {
            ...configToSave.appearance,
            avatarUrl:
              serverBot?.avatarUrl || configToSave.appearance.avatarUrl,
            launcherImageUrl:
              serverBot?.launcherImageUrl ||
              configToSave.appearance.launcherImageUrl,
            avatarFile: undefined,
            launcherFile: undefined,
          };

          await updateDeploymentMutation({
            variables: {
              deploymentId: widgetDeployment.deploymentId,
              widgetConfig: {
                appearance: cleanAppearance,
                behavior: configToSave.behavior,
              },
            },
          });
        }

        enqueueSnackbar("Bot settings updated", { variant: "success" });
        navigate("/settings/chatbot-widgets");
      }
    } catch (error) {
      console.error("Error saving bot:", error);
      enqueueSnackbar("Error saving bot configuration", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (newConfig: ChatbotConfig) => {
    setConfig(newConfig);
  };

  if (loading)
    return (
      <Box p={3}>
        <Typography>Loading editor...</Typography>
      </Box>
    );

  return (
    <Box className={classes.root}>
      <Grid
        container
        alignItems="center"
        sx={{
          mb: 3,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 0,
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={3}
          sx={{
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-start",
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/settings/chatbot-widgets")}
            className={classes.boxTextBack}
            sx={{ textTransform: "none" }}
          >
            Back to Widgets
          </Button>
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <Typography
            variant="h5"
            className={classes.boxText}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.5rem" },
              justifyContent: "center",
              width: "100%",
              textAlign: "center",
            }}
          >
            {isNewWidget ? "Create New Widget" : "Edit Widget"}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={3}
          sx={{ display: { xs: "none", md: "block" } }}
        />
      </Grid>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Widget Name"
          value={config.name || ""}
          onChange={(e) => handleChange({ ...config, name: e.target.value })}
          fullWidth
          required
          size="small"
          placeholder="e.g. Homepage Bot"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Widget Description"
          value={config.description || ""}
          onChange={(e) =>
            handleChange({ ...config, description: e.target.value })
          }
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="Brief description of this bot's purpose"
        />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        orientation={isMobile ? "vertical" : "horizontal"}
        variant={isMobile ? "standard" : "scrollable"}
        sx={{
          mb: 3,
          "& .MuiTabs-flexContainer": {
            flexDirection: isMobile ? "column" : "row",
          },
          "& .MuiTab-root": {
            alignItems: isMobile ? "flex-start" : "center",
            textAlign: "left",
            minHeight: isMobile ? 48 : 72,
          },
          borderBottom: isMobile ? 0 : 1,
          borderRight: isMobile ? 1 : 0,
          borderColor: "divider",
        }}
      >
        <Tab label="UI Configuration" />

        <Tab
          label={
            isNewWidget ? (
              <Tooltip
                title="Save configuration first to enable this tab"
                arrow
                placement="top"
              >
                <span>Data Ingestion</span>
              </Tooltip>
            ) : (
              "Data Ingestion"
            )
          }
          disabled={isNewWidget}
          sx={{
            "&.Mui-disabled": {
              pointerEvents: "auto",
            },
          }}
        />

        <Tab
          label={
            isNewWidget ? (
              <Tooltip
                title="Save configuration first to enable this tab"
                arrow
                placement="top"
              >
                <span>Script</span>
              </Tooltip>
            ) : (
              "Script"
            )
          }
          disabled={isNewWidget}
          sx={{
            "&.Mui-disabled": {
              pointerEvents: "auto",
            },
          }}
        />
      </Tabs>

      {activeTab === 0 && (
        <ConfigurationTab
          entityId={config.entityId}
          entityType="widget"
          config={config}
          onSave={handleSave}
          onChange={handleChange}
          loading={isSaving}
        />
      )}

      {activeTab === 1 && (
        <KnowledgeBaseTab
          botId={config.id}
          vectorNamespace={config.vectorNamespace}
        />
      )}

      {activeTab === 2 && (
        <InstallTab
          botId={config.id}
          deploymentId={
            data?.getBot?.deployments?.find(
              (d: any) => d.channelType === "widget",
            )?.deploymentId
          }
          config={config}
        />
      )}
    </Box>
  );
};

export default WidgetEditor;
